import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { SerializedError } from '@reduxjs/toolkit';

import type { FreeFloorPlanEligibility } from '../../../../types/order.types';
import { slugKey } from './floorPlanOffer';

/**
 * DV-3697: does the free floor plan rollout cover an account? order-service
 * answers per account (DV-3609) from the same code that adds the line when an
 * order is created, so the cart offers the free plan exactly where the order
 * will get it.
 *
 * - `covered`: the account's orders get the line. Carries the rollout's
 *   floor plan products, so the caller can tell which properties already have
 *   one.
 * - `not-covered`: they do not, or the account is not one the caller can check
 *   out for (403). Nothing to report.
 * - `pending`: no answer yet. Offer nothing; nothing to report.
 * - `undecided`: order-service could not decide (`offered: null`), answered
 *   with no usable `offered`, or the read failed. Offer nothing, and report it
 *   to ops (`cause`).
 */
export type FloorPlanCoverage =
  | { status: 'covered'; floorPlanSlugs: ReadonlySet<string> }
  | { status: 'not-covered' }
  | { status: 'pending' }
  | { status: 'undecided'; cause: string };

/** The parts of an RTK Query cache entry the coverage is decided from. */
export interface EligibilityRead {
  data?: FreeFloorPlanEligibility | null;
  error?: FetchBaseQueryError | SerializedError;
}

/**
 * A 403 whatever its body: one with a non-JSON body (a gateway, WAF or Spring
 * Security page) reaches us as `PARSING_ERROR` carrying the real status.
 */
function isForbidden(error: FetchBaseQueryError | SerializedError): boolean {
  if (!('status' in error)) return false;
  if (error.status === 403) return true;
  return error.status === 'PARSING_ERROR' && error.originalStatus === 403;
}

function errorCause(error: FetchBaseQueryError | SerializedError): string {
  if (!('status' in error)) return 'error';
  if (typeof error.status === 'number') return `http-${error.status}`;
  return error.status.toLowerCase();
}

export function toFloorPlanCoverage(read: EligibilityRead): FloorPlanCoverage {
  // RTK keeps the last answer when a refetch fails; it is at most a cache
  // lifetime old, so keep using it rather than flicker the offer away.
  if (read.data !== undefined) {
    // Only a boolean is an answer. A body without one (an older build, a
    // proxy's 200, an empty body read as null) must be reported, not read as
    // a quiet "no" or left pending.
    const offered: unknown = read.data?.offered;
    if (offered === true) {
      return {
        status: 'covered',
        floorPlanSlugs: new Set((read.data?.floorPlanSlugs ?? []).map(slugKey))
      };
    }
    if (offered === false) return { status: 'not-covered' };
    return {
      status: 'undecided',
      cause: offered === null ? 'offered-null' : 'offered-missing'
    };
  }
  if (read.error) {
    if (isForbidden(read.error)) return { status: 'not-covered' };
    return { status: 'undecided', cause: errorCause(read.error) };
  }
  return { status: 'pending' };
}
